/**
 * The feed's paging, wired to the DOM: touch drags, mouse wheel and arrow keys all end in one
 * page turn (see `pager.ts` for the rules). The feed itself never scrolls — the viewport clips
 * and each screen is moved with a transform — so the browser has nothing to snap, nothing to
 * ease, and no page scroll that would make its toolbars slide and resize the screen.
 *
 * "Resting" on a screen, which is what the feed's learning rules react to, now simply means a
 * page turn has finished: there are no screens swept past on the way to tell apart.
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AXIS_SLOP_PX, PAGE_MS, dragOffset, releaseTarget, releaseVelocity } from "./pager.js";

/** `animate` turns the page; `jump` swaps what is shown without motion (spec 6.1). */
export type Move = "animate" | "jump";

/**
 * The gesture belongs to an element that scrolls on its own, not to the feed: the credit sheet
 * (`data-own-scroll="always"`), or a question card on a very short screen once it actually has
 * something to scroll.
 */
function ownScroller(target: EventTarget | null): boolean {
  const element =
    target instanceof Element ? target.closest<HTMLElement>("[data-own-scroll]") : null;
  if (!element) return false;
  if (element.dataset["ownScroll"] === "always") return true;
  return element.scrollHeight > element.clientHeight + 1;
}

function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

export function usePager({
  count,
  initialIndex,
  onRest,
}: {
  count: number;
  initialIndex: number;
  /** Called once a page turn has finished on a screen other than the last one rested on. */
  onRest: (index: number) => void;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(initialIndex);
  const [moving, setMoving] = useState(false);
  const restTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rested = useRef(initialIndex);

  // The listeners are attached once; they read the current values from here.
  const live = useRef({ index, count, onRest, moving });
  useLayoutEffect(() => {
    live.current = { index, count, onRest, moving };
  });

  const setDrag = useCallback((px: number) => {
    viewport.current?.style.setProperty("--drag", px + "px");
  }, []);

  const finishOn = useCallback((target: number) => {
    clearTimeout(restTimer.current);
    restTimer.current = setTimeout(() => {
      setMoving(false);
      if (rested.current === target) return;
      rested.current = target;
      live.current.onRest(target);
    }, PAGE_MS);
  }, []);

  /** For the feed's own moves: the resume position, the checkpoint, the result, a new chapter. */
  const moveTo = useCallback(
    (target: number, how: Move) => {
      clearTimeout(restTimer.current);
      setDrag(0);
      setIndex(target);
      setMoving(how === "animate");
      if (how === "animate") finishOn(target);
      else rested.current = target;
    },
    [finishOn, setDrag],
  );

  // A layout effect, so the listeners are in place before the feed is first painted: with a
  // plain effect, a swipe or key press in the moment after the first paint was lost.
  useLayoutEffect(() => {
    const root = viewport.current;
    if (!root) return;

    const clamp = (target: number) => Math.min(Math.max(target, 0), live.current.count - 1);

    /** A release: commit the new screen and the transition before the drag offset is cleared,
     * so the browser animates from where the finger left the card rather than jumping. */
    const release = (target: number) => {
      flushSync(() => {
        setIndex(target);
        setMoving(true);
      });
      setDrag(0);
      finishOn(target);
    };

    const step = (direction: 1 | -1) => {
      const target = clamp(live.current.index + direction);
      if (target !== live.current.index) release(target);
    };

    let gesture: {
      x: number;
      y: number;
      /** Where the card already was when the finger landed mid-turn. */
      base: number;
      interrupted: boolean;
      axis: "x" | "y" | null;
      samples: { y: number; t: number }[];
    } | null = null;

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (event.touches.length !== 1 || !touch || ownScroller(event.target)) {
        gesture = null;
        return;
      }
      let base = 0;
      const interrupted = live.current.moving;
      if (interrupted) {
        // Catch the card where it is, so a quick second swipe continues from there.
        const slot = root.querySelector(`[data-index="${live.current.index}"]`);
        base = slot ? slot.getBoundingClientRect().top - root.getBoundingClientRect().top : 0;
        clearTimeout(restTimer.current);
        flushSync(() => setMoving(false));
        setDrag(base);
      }
      gesture = {
        x: touch.clientX,
        y: touch.clientY,
        base,
        interrupted,
        axis: null,
        samples: [{ y: touch.clientY, t: event.timeStamp }],
      };
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!gesture || !touch) return;
      const dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      if (!gesture.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_SLOP_PX) return;
        gesture.axis = Math.abs(dy) >= Math.abs(dx) ? "y" : "x";
      }
      if (gesture.axis !== "y") return;
      event.preventDefault();
      gesture.samples.push({ y: touch.clientY, t: event.timeStamp });
      if (gesture.samples.length > 12) gesture.samples.shift();
      setDrag(dragOffset(gesture.base + dy, live.current.index, live.current.count));
    };

    const onTouchEnd = (event: TouchEvent) => {
      const current = gesture;
      gesture = null;
      if (!current) return;
      if (current.axis !== "y") {
        // A tap, or a sideways swipe: nothing to turn, but finish a turn the finger caught.
        if (current.interrupted) release(live.current.index);
        return;
      }
      const last = current.samples[current.samples.length - 1] ?? { y: current.y };
      const target =
        event.type === "touchcancel"
          ? live.current.index
          : releaseTarget({
              index: live.current.index,
              count: live.current.count,
              dy: current.base + (last.y - current.y),
              velocity: releaseVelocity(current.samples),
              height: root.clientHeight,
            });
      release(target);
    };

    // A trackpad keeps sending wheel events for a second after the fingers lift; one swipe is
    // one page, so the wheel stays locked until those events stop.
    let wheelTotal = 0;
    let wheelLock: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (event: WheelEvent) => {
      if (ownScroller(event.target)) return;
      event.preventDefault();
      const unlock = () => {
        wheelLock = undefined;
        wheelTotal = 0;
      };
      if (wheelLock) {
        clearTimeout(wheelLock);
        wheelLock = setTimeout(unlock, 200);
        return;
      }
      wheelTotal += event.deltaY;
      if (Math.abs(wheelTotal) < 40) return;
      step(wheelTotal > 0 ? 1 : -1);
      wheelLock = setTimeout(unlock, 200);
    };

    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        step(1);
      }
      if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        step(-1);
      }
    };

    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    root.addEventListener("touchend", onTouchEnd);
    root.addEventListener("touchcancel", onTouchEnd);
    root.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(restTimer.current);
      clearTimeout(wheelLock);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchEnd);
      root.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
    };
  }, [finishOn, setDrag]);

  return { viewport, index, moving, moveTo };
}
